
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddCourseDialog } from "./courses/add-course";
import { toast } from "sonner";
import { SearchBar } from "./courses/SearchBar";
import { CoursesTable } from "./courses/CoursesTable";
import { CoursesError } from "./courses/CoursesError";
import { useCoursesSort } from "@/hooks/useCoursesSort";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { ResponsiveTable } from "@/components/ui/responsive-table";

type Course = {
  id: string;
  course_name: string;
  course_type: string;
  description: string;
  duration_hours: number;
  price: number;
  language: string;
};

export function CoursesContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const { sortConfig, handleSort, sortCourses } = useCoursesSort();
  const { adminUser, isAdmin } = useAdminAuth();
  const isMobile = useIsMobile();

  const fetchCourses = useCallback(async () => {
    console.log('Fetching courses using admin function...');
    
    try {
      // Call our RPC function that bypasses RLS
      const { data, error } = await supabase
        .rpc('get_all_course_offerings');
        
      if (error) {
        console.error('Error in fetchCourses:', error);
        throw error;
      }
      
      console.log('Courses fetched successfully:', data);
      return data as Course[];
    } catch (error) {
      console.error('Error in fetchCourses:', error);
      throw error;
    }
  }, []);

  const { 
    data: courses, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['courses'],
    queryFn: fetchCourses,
    retry: 3,
    retryDelay: 2000,
    meta: {
      onSettled: (data, error) => {
        if (error) {
          toast.error('Failed to load courses', {
            description: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }
    }
  });

  const filteredAndSortedCourses = sortCourses(
    courses?.filter(course => 
      course.course_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.course_type.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []
  );

  if (error) {
    return <CoursesError error={error} onRetry={() => refetch()} />;
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 flex flex-col items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-muted-foreground">Loading courses...</p>
      </div>
    );
  }

  if (!courses || courses.length === 0) {
    return (
      <div className="p-4 md:p-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Courses</h1>
          <div className="mobile-action-group mb-4 md:mb-6">
            <div className="w-full md:w-auto">
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </div>
            <div className="flex gap-2 mt-2 md:mt-0">
              <Button 
                onClick={() => refetch()} 
                variant="outline" 
                size="icon"
                title="Refresh courses"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <AddCourseDialog />
            </div>
          </div>
          
          <div className="bg-muted/40 rounded-lg p-4 md:p-8 text-center">
            <p className="text-muted-foreground mb-4">No courses found. Add your first course to get started.</p>
            <AddCourseDialog />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Courses</h1>
        <div className="mobile-action-group">
          <div className="w-full md:w-auto mb-2 md:mb-0">
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => refetch()} 
              variant="outline" 
              size={isMobile ? "sm" : "icon"}
              title="Refresh courses"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <AddCourseDialog />
          </div>
        </div>
      </div>

      <ResponsiveTable compact={isMobile}>
        <CoursesTable 
          courses={filteredAndSortedCourses}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </ResponsiveTable>
    </div>
  );
}
